import { api } from '@/lib/http';

export type RecallRecordingStatus = {
  code?: string;
  sub_code?: string | null;
  updated_at?: string;
};

export type RecallMediaShortcutEntry = {
  status?: RecallRecordingStatus;
  data?: { download_url?: string | null };
  download_url?: string | null;
};

export type RecallRecording = {
  id: string;
  status?: RecallRecordingStatus;
  created_at?: string;
  started_at?: string | null;
  completed_at?: string | null;
  expires_at?: string | null;
  metadata?: Record<string, unknown>;
  bot?: unknown;
  sdk_upload?: { id?: string; metadata?: Record<string, unknown> } | null;
  media_shortcuts?: {
    transcript?: RecallMediaShortcutEntry;
    video_mixed?: RecallMediaShortcutEntry;
    audio_mixed?: RecallMediaShortcutEntry;
    [key: string]: RecallMediaShortcutEntry | undefined;
  };
  participants?: Array<{ id?: number | string; name?: string | null; is_host?: boolean }>;
};

export type RecallRecordingsPage = {
  next: string | null;
  previous: string | null;
  results: RecallRecording[];
};

export type RecallTranscriptParticipant = {
  id?: number | string;
  name?: string | null;
  is_host?: boolean;
  speaker_id?: number | string;
};

export type RecallTranscriptWord = {
  text: string;
  start_timestamp?: { relative?: number; absolute?: string };
  end_timestamp?: { relative?: number; absolute?: string };
};

export type RecallTranscriptSegment = {
  participant?: RecallTranscriptParticipant;
  words: RecallTranscriptWord[];
};

export type RecallTranscriptResponse = {
  ready: boolean;
  status: string;
  transcript: RecallTranscriptSegment[] | null;
};

export type ListRecordingsParams = {
  cursor?: string;
  limit?: number;
  ordering?: string;
  sdkUploadOnly?: boolean;
};

export const recallApi = {
  async listRecordings(params: ListRecordingsParams = {}): Promise<RecallRecordingsPage> {
    const response = await api.get<RecallRecordingsPage>('/v2/recall/recordings', {
      params: {
        ...(params.cursor ? { cursor: params.cursor } : {}),
        ...(params.limit ? { limit: params.limit } : {}),
        ...(params.ordering ? { ordering: params.ordering } : {}),
        ...(params.sdkUploadOnly ? { sdk_upload_only: 'true' } : {}),
      },
    });
    return response.data;
  },

  async getRecording(recordingId: string): Promise<RecallRecording> {
    const response = await api.get<RecallRecording>(
      `/v2/recall/recordings/${encodeURIComponent(recordingId)}`,
    );
    return response.data;
  },

  async getTranscript(recordingId: string): Promise<RecallTranscriptResponse> {
    const response = await api.get<RecallTranscriptResponse>(
      `/v2/recall/recordings/${encodeURIComponent(recordingId)}/transcript`,
    );
    return response.data;
  },

  async createSdkUpload(metadata?: Record<string, unknown>): Promise<{
    id: string;
    upload_token: string;
    recording_id?: string;
  }> {
    const response = await api.post('/v2/recall/sdk-upload', {
      recording_config: { transcript: { provider: { recallai_streaming: {} } } },
      ...(metadata ? { metadata } : {}),
    });
    return response.data;
  },

  async deleteRecording(recordingId: string): Promise<void> {
    await api.delete(`/v2/recall/recordings/${encodeURIComponent(recordingId)}`);
  },
};
